import { useEffect, useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { api, fmtMoney, fmtNumber, fmtDate } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Printer,
  DownloadSimple,
  CircleNotch,
  ArrowDown,
  ArrowUp,
  FunnelSimple,
  X,
} from "@phosphor-icons/react";

const ALL = "__all__";

function Metric({ label, value, sub }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-md p-5">
      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#64748B]">{label}</p>
      <p className="text-2xl font-black tracking-tight text-[#0F172A] font-heading mt-1">{value}</p>
      {sub && <p className="text-xs text-[#64748B] mt-0.5">{sub}</p>}
    </div>
  );
}

export default function ReportePersonalizado() {
  const [productos, setProductos] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [productoId, setProductoId] = useState(ALL);
  const [numeroLote, setNumeroLote] = useState(ALL);
  const [tipo, setTipo] = useState(ALL);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/productos").then((r) => setProductos(r.data));
    api.get("/lotes").then((r) => setLotes(r.data));
  }, []);

  // Nº de lote únicos (una compra puede tener varios productos => varios documentos de lote)
  const numerosLote = (() => {
    const map = new Map();
    lotes.forEach((l) => {
      const n = l.numero_lote;
      if (!n) return;
      map.set(n, (map.get(n) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([numero, count]) => ({ numero, count }))
      .sort((a, b) => b.numero.localeCompare(a.numero));
  })();

  const generar = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (desde) params.set("desde", desde);
    if (hasta) params.set("hasta", hasta);
    if (productoId !== ALL) params.set("producto_id", productoId);
    if (numeroLote !== ALL) params.set("numero_lote", numeroLote);
    if (tipo !== ALL) params.set("tipo", tipo);
    api
      .get(`/reporte-personalizado?${params.toString()}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  };

  const limpiar = () => {
    setDesde("");
    setHasta("");
    setProductoId(ALL);
    setNumeroLote(ALL);
    setTipo(ALL);
    setData(null);
  };

  const resumenFiltros = () => {
    const partes = [];
    if (desde) partes.push(`Desde ${desde}`);
    if (hasta) partes.push(`Hasta ${hasta}`);
    if (productoId !== ALL) {
      const p = productos.find((x) => x.id === productoId);
      if (p) partes.push(`Producto: ${p.nombre}`);
    }
    if (numeroLote !== ALL) partes.push(`Lote: ${numeroLote}`);
    if (tipo !== ALL) partes.push(`Tipo: ${tipo === "ingreso" ? "Ingresos" : "Salidas"}`);
    return partes.length ? partes.join(" · ") : "Todos los movimientos";
  };

  const fileBase = () => {
    const d = desde || "inicio";
    const h = hasta || "hoy";
    return `reporte_personalizado_${d}_${h}`;
  };

  const exportPDF = () => {
    if (!data) return;
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text("Reporte personalizado", 14, 18);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(resumenFiltros(), 14, 25, { maxWidth: 180 });

    autoTable(doc, {
      startY: 34,
      theme: "plain",
      body: [
        ["Movimientos", fmtNumber(data.num_movimientos)],
        ["Total ingresos", fmtNumber(data.total_ingresos) + " u"],
        ["Total salidas", fmtNumber(data.total_salidas) + " u"],
        ["Costo de ingresos", fmtMoney(data.costo_total_ingresos)],
        ["Stock final total", fmtNumber(data.stock_final_total) + " u"],
        ["Valor stock final", fmtMoney(data.valor_final_total)],
      ],
      styles: { fontSize: 10 },
      columnStyles: { 0: { fontStyle: "bold", textColor: [15, 23, 42] } },
    });

    if ((data.lotes_detalle || []).length) {
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head: [["Lote", "Producto", "Stock inicial", "Ingresos", "Salidas", "Stock final", "Valor final"]],
        body: data.lotes_detalle.map((l) => [
          l.numero_lote,
          `${l.nombre} (${l.codigo})`,
          fmtNumber(l.stock_inicial),
          "+" + fmtNumber(l.ingresos),
          "-" + fmtNumber(l.salidas),
          fmtNumber(l.stock_final),
          fmtMoney(l.valor_final),
        ]),
        headStyles: { fillColor: [15, 23, 42] },
        styles: { fontSize: 9 },
      });
    }

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [["Fecha", "Producto", "Lote", "Tipo", "Cantidad", "Costo u."]],
      body: data.movimientos.map((m) => [
        fmtDate(m.fecha_movimiento),
        m.lote_nombre,
        m.numero_lote,
        m.tipo === "ingreso" ? "Ingreso" : "Salida",
        fmtNumber(m.cantidad),
        m.costo_unitario != null ? fmtMoney(m.costo_unitario) : "—",
      ]),
      headStyles: { fillColor: [15, 23, 42] },
      styles: { fontSize: 9 },
    });

    doc.save(`${fileBase()}.pdf`);
  };

  const exportCSV = () => {
    if (!data) return;
    const cell = (v) => {
      if (typeof v === "number") return String(v).replace(".", ",");
      const s = String(v ?? "");
      return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = [];
    rows.push(["Reporte personalizado"]);
    rows.push([resumenFiltros()]);
    rows.push([]);
    rows.push(["Resumen"]);
    rows.push(["Movimientos", data.num_movimientos]);
    rows.push(["Total ingresos (u)", data.total_ingresos]);
    rows.push(["Total salidas (u)", data.total_salidas]);
    rows.push(["Costo de ingresos", data.costo_total_ingresos]);
    rows.push(["Stock final total (u)", data.stock_final_total]);
    rows.push(["Valor stock final", data.valor_final_total]);
    rows.push([]);
    rows.push(["Stock final por lote"]);
    rows.push(["Lote", "Producto", "Codigo", "Stock inicial", "Ingresos", "Salidas", "Stock final", "Valor final"]);
    (data.lotes_detalle || []).forEach((l) => {
      rows.push([l.numero_lote, l.nombre, l.codigo, l.stock_inicial, l.ingresos, l.salidas, l.stock_final, l.valor_final]);
    });
    rows.push([]);
    rows.push(["Detalle de movimientos"]);
    rows.push(["Fecha", "Producto", "Lote", "Tipo", "Cantidad", "Costo unitario", "Motivo"]);
    data.movimientos.forEach((m) => {
      rows.push([
        m.fecha_movimiento,
        m.lote_nombre,
        m.numero_lote,
        m.tipo,
        m.cantidad,
        m.costo_unitario ?? "",
        m.motivo,
      ]);
    });
    const csv = rows.map((r) => r.map(cell).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileBase()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportXLSX = () => {
    if (!data) return;
    const toDate = (iso) => {
      const p = (iso || "").slice(0, 10).split("-");
      return p.length === 3 ? new Date(+p[0], +p[1] - 1, +p[2]) : iso;
    };
    const wb = XLSX.utils.book_new();

    const resumen = [
      ["Reporte personalizado"],
      [resumenFiltros()],
      [],
      ["Movimientos", data.num_movimientos],
      ["Total ingresos (u)", data.total_ingresos],
      ["Total salidas (u)", data.total_salidas],
      ["Costo de ingresos", data.costo_total_ingresos],
      ["Stock final total (u)", data.stock_final_total],
      ["Valor stock final", data.valor_final_total],
    ];
    const wsR = XLSX.utils.aoa_to_sheet(resumen);
    wsR["!cols"] = [{ wch: 26 }, { wch: 18 }];
    XLSX.utils.book_append_sheet(wb, wsR, "Resumen");

    const stockRows = [["Lote", "Producto", "Código", "Stock inicial", "Ingresos", "Salidas", "Stock final", "Valor final"]];
    (data.lotes_detalle || []).forEach((l) =>
      stockRows.push([l.numero_lote, l.nombre, l.codigo, l.stock_inicial, l.ingresos, l.salidas, l.stock_final, l.valor_final]));
    const wsS = XLSX.utils.aoa_to_sheet(stockRows);
    wsS["!cols"] = [{ wch: 16 }, { wch: 24 }, { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 16 }];
    XLSX.utils.book_append_sheet(wb, wsS, "Stock final");

    const movRows = [["Fecha", "Producto", "Lote", "Tipo", "Cantidad", "Costo unitario", "Motivo"]];
    data.movimientos.forEach((m) =>
      movRows.push([
        toDate(m.fecha_movimiento),
        m.lote_nombre,
        m.numero_lote,
        m.tipo,
        m.cantidad,
        m.costo_unitario ?? "",
        m.motivo,
      ]));
    const wsM = XLSX.utils.aoa_to_sheet(movRows, { cellDates: true });
    // Formato de fecha en la columna A
    const ref = XLSX.utils.decode_range(wsM["!ref"]);
    for (let r = 1; r <= ref.e.r; r++) {
      const addr = XLSX.utils.encode_cell({ r, c: 0 });
      if (wsM[addr] && (wsM[addr].t === "d" || wsM[addr].t === "n")) wsM[addr].z = "dd-mm-yyyy";
    }
    wsM["!cols"] = [{ wch: 12 }, { wch: 24 }, { wch: 16 }, { wch: 10 }, { wch: 10 }, { wch: 14 }, { wch: 24 }];
    XLSX.utils.book_append_sheet(wb, wsM, "Movimientos");

    XLSX.writeFile(wb, `${fileBase()}.xlsx`);
  };

  return (
    <div className="space-y-6" data-testid="reporte-personalizado">
      {/* Filtros */}
      <div className="bg-white border border-[#E2E8F0] rounded-md p-6">
        <div className="flex items-center gap-2 mb-4">
          <FunnelSimple size={18} weight="bold" className="text-[#0F172A]" />
          <h2 className="font-heading text-lg font-bold text-[#0F172A]">Filtros del reporte</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Desde</Label>
            <Input
              type="date"
              data-testid="filtro-desde"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              className="bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Hasta</Label>
            <Input
              type="date"
              data-testid="filtro-hasta"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              className="bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Tipo</Label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger data-testid="filtro-tipo" className="bg-white border-[#E2E8F0]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todos</SelectItem>
                <SelectItem value="ingreso">Ingresos</SelectItem>
                <SelectItem value="salida">Salidas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Producto</Label>
            <Select
              value={productoId}
              onValueChange={(v) => {
                setProductoId(v);
                if (v !== ALL) setNumeroLote(ALL);
              }}
            >
              <SelectTrigger data-testid="filtro-producto" className="bg-white border-[#E2E8F0]">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todos los productos</SelectItem>
                {productos.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.nombre} ({p.codigo})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Nº de lote (compra)</Label>
            <Select
              value={numeroLote}
              onValueChange={(v) => {
                setNumeroLote(v);
                if (v !== ALL) setProductoId(ALL);
              }}
            >
              <SelectTrigger data-testid="filtro-lote" className="bg-white border-[#E2E8F0]">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todos los lotes</SelectItem>
                {numerosLote.map((n) => (
                  <SelectItem key={n.numero} value={n.numero}>
                    {n.numero}{n.count > 1 ? ` · ${n.count} productos` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Button
              data-testid="generar-reporte-button"
              onClick={generar}
              disabled={loading}
              className="h-10 flex-1 bg-[#0F172A] hover:bg-[#1E293B] text-white font-semibold"
            >
              {loading ? <CircleNotch size={18} className="animate-spin" /> : "Generar reporte"}
            </Button>
            <Button
              data-testid="limpiar-filtros-button"
              variant="outline"
              onClick={limpiar}
              className="h-10 border-[#E2E8F0]"
              title="Limpiar filtros"
            >
              <X size={18} />
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <CircleNotch size={32} className="animate-spin text-[#0F172A]" />
        </div>
      ) : !data ? (
        <div className="bg-white border border-dashed border-[#E2E8F0] rounded-md px-6 py-16 text-center">
          <FunnelSimple size={40} weight="duotone" className="mx-auto text-[#94A3B8] mb-3" />
          <p className="text-[#64748B]">Configura los filtros y pulsa "Generar reporte".</p>
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-[#475569]" data-testid="filtros-resumen">{resumenFiltros()}</p>
            <div className="flex gap-2">
              <Button data-testid="custom-export-pdf" variant="outline" onClick={exportPDF} className="border-[#E2E8F0]">
                <Printer size={18} className="mr-1.5" /> Exportar PDF
              </Button>
              <Button data-testid="custom-export-csv" variant="outline" onClick={exportCSV} className="border-[#E2E8F0]">
                <DownloadSimple size={18} className="mr-1.5" /> Exportar CSV
              </Button>
              <Button data-testid="custom-export-xlsx" variant="outline" onClick={exportXLSX} className="border-[#E2E8F0]">
                <DownloadSimple size={18} className="mr-1.5" /> Exportar Excel
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
            <Metric label="Movimientos" value={fmtNumber(data.num_movimientos)} sub="encontrados" />
            <Metric label="Total ingresos" value={fmtNumber(data.total_ingresos)} sub="unidades" />
            <Metric label="Total salidas" value={fmtNumber(data.total_salidas)} sub="unidades" />
            <Metric label="Costo de ingresos" value={fmtMoney(data.costo_total_ingresos)} sub="compras" />
            <Metric label="Stock final" value={fmtNumber(data.stock_final_total)} sub={hasta ? `al ${hasta}` : "actual"} />
            <Metric label="Valor stock final" value={fmtMoney(data.valor_final_total)} sub="valorización" />
          </div>

          {/* Stock final por lote / producto */}
          <div className="bg-white border border-[#E2E8F0] rounded-md overflow-hidden">
            <div className="px-6 py-4 border-b border-[#E2E8F0]">
              <h2 className="font-heading text-lg font-bold text-[#0F172A]">Stock final por lote</h2>
            </div>
            {(data.lotes_detalle || []).length === 0 ? (
              <p className="px-6 py-12 text-center text-[#64748B]">Sin lotes para estos filtros.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="custom-stock-table">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-[#64748B] bg-[#F8F9FA]">
                      <th className="px-6 py-3 font-semibold">Lote</th>
                      <th className="px-6 py-3 font-semibold">Producto</th>
                      <th className="px-6 py-3 font-semibold text-right">Stock inicial</th>
                      <th className="px-6 py-3 font-semibold text-right">Ingresos</th>
                      <th className="px-6 py-3 font-semibold text-right">Salidas</th>
                      <th className="px-6 py-3 font-semibold text-right">Stock final</th>
                      <th className="px-6 py-3 font-semibold text-right">Valor final</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.lotes_detalle.map((l, i) => (
                      <tr key={i} className="border-t border-[#E2E8F0] hover:bg-[#F8F9FA] transition-colors">
                        <td className="px-6 py-3 font-semibold text-[#0F172A]">{l.numero_lote}</td>
                        <td className="px-6 py-3">
                          <div className="font-medium text-[#0F172A]">{l.nombre}</div>
                          <div className="text-xs text-[#64748B]">{l.codigo}</div>
                        </td>
                        <td className="px-6 py-3 text-right text-[#475569]">{fmtNumber(l.stock_inicial)}</td>
                        <td className="px-6 py-3 text-right text-[#065F46] font-medium">+{fmtNumber(l.ingresos)}</td>
                        <td className="px-6 py-3 text-right text-[#991B1B] font-medium">−{fmtNumber(l.salidas)}</td>
                        <td className="px-6 py-3 text-right font-bold text-[#0F172A]">{fmtNumber(l.stock_final)}</td>
                        <td className="px-6 py-3 text-right text-[#0F172A]">{fmtMoney(l.valor_final)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-[#E2E8F0] bg-[#F8F9FA] font-bold text-[#0F172A]">
                      <td className="px-6 py-3" colSpan={5}>Totales</td>
                      <td className="px-6 py-3 text-right" data-testid="custom-stock-final-total">{fmtNumber(data.stock_final_total)}</td>
                      <td className="px-6 py-3 text-right" data-testid="custom-valor-final-total">{fmtMoney(data.valor_final_total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-md overflow-hidden">
            <div className="px-6 py-4 border-b border-[#E2E8F0]">
              <h2 className="font-heading text-lg font-bold text-[#0F172A]">Detalle de movimientos</h2>
            </div>
            {data.movimientos.length === 0 ? (
              <p className="px-6 py-12 text-center text-[#64748B]">Sin movimientos para estos filtros.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="custom-movimientos-table">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-[#64748B] bg-[#F8F9FA]">
                      <th className="px-6 py-3 font-semibold">Fecha</th>
                      <th className="px-6 py-3 font-semibold">Producto / Lote</th>
                      <th className="px-6 py-3 font-semibold">Tipo</th>
                      <th className="px-6 py-3 font-semibold text-right">Cantidad</th>
                      <th className="px-6 py-3 font-semibold text-right">Costo u.</th>
                      <th className="px-6 py-3 font-semibold">Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.movimientos.map((m) => (
                      <tr key={m.id} className="border-t border-[#E2E8F0] hover:bg-[#F8F9FA] transition-colors">
                        <td className="px-6 py-3 text-[#64748B]">{fmtDate(m.fecha_movimiento)}</td>
                        <td className="px-6 py-3">
                          <div className="font-medium text-[#0F172A]">{m.lote_nombre}</div>
                          <div className="text-xs text-[#64748B]">{m.numero_lote}</div>
                        </td>
                        <td className="px-6 py-3">
                          <span className={`inline-flex items-center gap-1 text-xs font-bold ${m.tipo === "ingreso" ? "text-[#065F46]" : "text-[#991B1B]"}`}>
                            {m.tipo === "ingreso" ? <ArrowDown size={12} weight="bold" /> : <ArrowUp size={12} weight="bold" />}
                            {m.tipo === "ingreso" ? "Ingreso" : "Salida"}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-right font-semibold text-[#0F172A]">{fmtNumber(m.cantidad)}</td>
                        <td className="px-6 py-3 text-right text-[#475569]">{m.costo_unitario != null ? fmtMoney(m.costo_unitario) : "—"}</td>
                        <td className="px-6 py-3 text-[#475569]">{m.motivo}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
